import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../../components/BrandLogo';
import AppVersion from '../../components/AppVersion';
import { sendOtp } from '../../services/authApi';
import { deregisterAccount } from '../../services/accountApi';
import { applyOtpDigits, useOtpAutofill } from '../../hooks/useOtpAutofill';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../config/constants';
import './AccountDeregister.css';

const normalizeMobile = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length <= 10) return digits;
  return digits.slice(-10);
};

const AccountDeregister = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const [step, setStep] = useState('mobile');
  const [mobile, setMobile] = useState('');
  const [reqId, setReqId] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useOtpAutofill(setOtp, { enabled: step === 'verify' && Boolean(mobile && reqId) });

  useEffect(() => {
    const storedMobile = user?.mobile;
    if (storedMobile && storedMobile.length === 10) {
      setMobile(storedMobile);
    }
  }, [user]);

  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const timer = setInterval(() => {
      setResendTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendTimer]);

  const clearOtpInputs = useCallback(() => {
    setOtp(['', '', '', '', '', '']);
    requestAnimationFrame(() => {
      document.getElementById('deregister-otp-0')?.focus();
    });
  }, []);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const normalized = normalizeMobile(mobile);
    if (normalized.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      setLoading(false);
      return;
    }

    try {
      const response = await sendOtp(normalized);
      const nextReqId = response.data?.reqId;
      if (!nextReqId) {
        throw new Error(response.data?.message || 'Failed to send OTP');
      }
      setMobile(normalized);
      setReqId(nextReqId);
      setResendTimer(60);
      setStep('verify');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0 || loading) return;
    setError('');
    setLoading(true);
    try {
      const response = await sendOtp(mobile);
      const nextReqId = response.data?.reqId;
      if (!nextReqId) {
        throw new Error('Failed to resend OTP');
      }
      setReqId(nextReqId);
      setResendTimer(60);
      clearOtpInputs();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setError('');
    if (value && index < 5) {
      document.getElementById(`deregister-otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`deregister-otp-${index - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData?.getData('text') || '';
    if (applyOtpDigits(pasted, setOtp)) {
      e.preventDefault();
      document.getElementById('deregister-otp-5')?.focus();
    }
  };

  const handleDeregister = async (e) => {
    e.preventDefault();
    setError('');

    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }
    if (!confirmed) {
      setError('Please confirm that you understand your data will be permanently deleted');
      return;
    }

    setLoading(true);
    try {
      await deregisterAccount(mobile, reqId, otpString);
      logout();
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete account');
      clearOtpInputs();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-deregister-container">
      <div className="account-deregister-card">
        <div className="account-deregister-header">
          <BrandLogo variant="compact" className="account-deregister-logo" />
          <h1 className="account-deregister-title">Delete account</h1>
        </div>

        {step === 'mobile' && (
          <>
            <p className="account-deregister-subtitle">
              Enter your registered mobile number. We will send an OTP to verify your identity before deleting your account.
            </p>
            {error && <div className="account-deregister-error">{error}</div>}
            <form onSubmit={handleSendOtp} className="account-deregister-form">
              <div className="account-deregister-input-group">
                <label htmlFor="deregister-mobile">Mobile number</label>
                <input
                  id="deregister-mobile"
                  type="tel"
                  value={mobile}
                  onChange={(e) => {
                    setMobile(normalizeMobile(e.target.value));
                    setError('');
                  }}
                  placeholder="10-digit mobile number"
                  required
                  autoComplete="tel"
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                />
              </div>
              <button type="submit" className="account-deregister-btn" disabled={loading || mobile.length < 10}>
                {loading ? 'Sending OTP…' : 'Send OTP'}
              </button>
            </form>
          </>
        )}

        {step === 'verify' && (
          <>
            <p className="account-deregister-subtitle">
              Enter the OTP sent to <strong>+91 {mobile}</strong>. This action cannot be undone.
            </p>
            {error && <div className="account-deregister-error">{error}</div>}
            <form onSubmit={handleDeregister} className="account-deregister-form">
              <div className="account-deregister-otp-row" onPaste={handleOtpPaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`deregister-otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="account-deregister-otp-input"
                  />
                ))}
              </div>

              <label className="account-deregister-confirm">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => {
                    setConfirmed(e.target.checked);
                    setError('');
                  }}
                />
                <span>
                  I understand that my account, profile, transaction history, photos, and all other personal data will be permanently deleted.
                </span>
              </label>

              <button
                type="submit"
                className="account-deregister-btn account-deregister-btn-danger"
                disabled={loading || !confirmed || otp.join('').length !== 6}
              >
                {loading ? 'Deleting account…' : 'Delete my account permanently'}
              </button>

              <div className="account-deregister-actions">
                <button type="button" className="account-deregister-link" onClick={() => setStep('mobile')}>
                  Change mobile number
                </button>
                <button
                  type="button"
                  className="account-deregister-link"
                  onClick={handleResendOtp}
                  disabled={resendTimer > 0 || loading}
                >
                  {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                </button>
              </div>
            </form>
          </>
        )}

        {step === 'done' && (
          <div className="account-deregister-success">
            <p>Your account and all associated data have been permanently deleted.</p>
            <button
              type="button"
              className="account-deregister-btn"
              onClick={() => navigate(ROUTES.MOBILE_LOGIN, { replace: true })}
            >
              Done
            </button>
          </div>
        )}

        {step !== 'done' && (
          <div className="account-deregister-footer">
            <button
              type="button"
              className="account-deregister-link"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <AppVersion className="account-deregister-version" />
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountDeregister;
